import React, { useState } from 'react'
import {X, ChevronLeft, ChevronRight} from 'lucide-react'
import type { ProductV2Detail, Lang } from './types'

interface ProductHeroV2Props {
  product: ProductV2Detail
  currentLang: Lang
}

/**
 * ProductHeroV2
 *
 * Left-side full-bleed square product image (Barista Map style).
 * Click to open lightbox gallery if there are multiple images.
 * Cream background, no border, no shadow — pure visual focus.
 */
const ProductHeroV2: React.FC<ProductHeroV2Props> = ({ product, currentLang }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const isZh = currentLang === 'zh'
  const displayName = isZh ? product.name : (product.nameEn || product.name)
  const images = (product.images || []).filter(Boolean)
  const hasMultiple = images.length > 1

  const next = () => setActiveIndex((i) => (i === images.length - 1 ? 0 : i + 1))
  const prev = () => setActiveIndex((i) => (i === 0 ? images.length - 1 : i - 1))

  return (
    <>
      <div
        className={`relative aspect-square w-full overflow-hidden bg-[#F6F4EE] ${hasMultiple ? 'cursor-zoom-in' : ''}`}
        onClick={() => hasMultiple && setLightboxOpen(true)}
      >
        {images[0] ? (
          <img
            src={images[0]}
            alt={displayName}
            className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.02]"
            loading="eager"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-black/30">
            {isZh ? '無圖片' : 'No image'}
          </div>
        )}

        {hasMultiple && (
          <div
            className="absolute bottom-4 right-4 bg-black/70 px-3 py-1 text-[10px] text-white pointer-events-none"
            style={{ letterSpacing: '0.2em', fontFamily: "'EB Garamond', serif" }}
          >
            {isZh ? `${images.length} 張` : `${images.length} PHOTOS`}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
          >
            <X size={28} strokeWidth={1.5} />
          </button>

          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img
                src={images[activeIndex]}
                alt={`${displayName} ${activeIndex + 1}`}
                className="mx-auto max-h-[80vh] w-auto object-contain"
              />
              {hasMultiple && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white"
                    aria-label="Previous"
                  >
                    <ChevronLeft size={32} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white"
                    aria-label="Next"
                  >
                    <ChevronRight size={32} strokeWidth={1.5} />
                  </button>
                </>
              )}
            </div>

            {hasMultiple && (
              <div className="mt-6 flex justify-center gap-2 overflow-x-auto pb-2">
                {images.map((image, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveIndex(idx)}
                    className={`h-16 w-16 flex-shrink-0 overflow-hidden transition-opacity ${
                      activeIndex === idx ? 'opacity-100 ring-1 ring-white' : 'opacity-50 hover:opacity-80'
                    }`}
                  >
                    <img src={image} alt={`thumb ${idx + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div
              className="mt-3 text-center text-xs text-white/60"
              style={{ letterSpacing: '0.2em', fontFamily: "'EB Garamond', serif" }}
            >
              {activeIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ProductHeroV2
